import React, { useEffect, useState } from 'react';
import { X, TrendingUp, TrendingDown, Minus, Loader2, AlertCircle, Activity } from 'lucide-react';
import {
  getBlueprintPerformanceDetails,
  type BlueprintVersionStagePerformance,
} from '../services/blueprintPerformanceService';
import type { CardData } from '../types/card';

interface MapWorkflowComparePanelProps {
  blueprintId: string | null;
  organizationId: string;
  cards: CardData[];
  onClose: () => void;
}

interface CompareRow {
  name: string;
  estimatedTp: number | null;
  realAvgTp: number | null;
  totalTasks: number;
}

function formatMin(minutes: number | null): string {
  if (minutes === null) return '—';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  return `${(minutes / 60).toFixed(1)}h`;
}

function getDeltaPct(estimated: number | null, real: number | null): string {
  if (estimated === null || real === null || estimated === 0) return '—';
  const d = ((real - estimated) / estimated) * 100;
  return `${d > 0 ? '+' : ''}${Math.round(d)}%`;
}

export const MapWorkflowComparePanel: React.FC<MapWorkflowComparePanelProps> = ({
  blueprintId,
  organizationId,
  cards,
  onClose,
}) => {
  const [rows, setRows] = useState<CompareRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blueprintName, setBlueprintName] = useState<string | null>(null);
  const [totalExecutions, setTotalExecutions] = useState<number>(0);

  useEffect(() => {
    if (!blueprintId || !organizationId) {
      setRows([]);
      return;
    }
    setIsLoading(true);
    setError(null);

    getBlueprintPerformanceDetails({ blueprintId, organizationId })
      .then((details) => {
        const stageData: BlueprintVersionStagePerformance[] =
          details.versions?.[0]?.tempo_medio_por_etapa ?? [];

        if (details.metrics?.blueprintName) setBlueprintName(details.metrics.blueprintName);
        setTotalExecutions(details.metrics?.totalExecutions ?? 0);

        const activityCards = cards.filter(
          (c) => c.tipo === 'atividade' && c.status !== 'removido'
        );

        const result: CompareRow[] = activityCards.map((card, idx) => {
          // Match by name first, fall back to position order
          const stageMatch =
            stageData.find(
              (s) =>
                s.stage_name?.toLowerCase().trim() ===
                card.atividade?.toLowerCase().trim()
            ) ?? stageData[idx] ?? null;

          const estimatedTp =
            card.lt !== undefined && card.lt !== null
              ? Number(card.lt)
              : card.tp !== undefined && card.tp !== null
              ? Number(card.tp)
              : null;

          return {
            name: card.atividade || '(sem nome)',
            estimatedTp,
            realAvgTp: stageMatch?.tp_medio ?? null,
            totalTasks: stageMatch?.total_tasks ?? 0,
          };
        });

        setRows(result);
      })
      .catch((err: any) => {
        setError(err?.message || 'Erro ao carregar dados de performance');
      })
      .finally(() => setIsLoading(false));
  }, [blueprintId, organizationId, cards]);

  const getDeltaIcon = (estimated: number | null, real: number | null) => {
    if (estimated === null || real === null)
      return <Minus size={10} className="text-slate-400" />;
    if (real > estimated * 1.1) return <TrendingUp size={10} className="text-red-500" />;
    if (real < estimated * 0.9) return <TrendingDown size={10} className="text-emerald-500" />;
    return <Minus size={10} className="text-amber-500" />;
  };

  const getDeltaClass = (estimated: number | null, real: number | null): string => {
    if (estimated === null || real === null) return 'text-slate-400 dark:text-slate-500';
    if (real > estimated * 1.1) return 'text-red-500 font-semibold';
    if (real < estimated * 0.9) return 'text-emerald-600 dark:text-emerald-400 font-semibold';
    return 'text-amber-600 dark:text-amber-400';
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-[var(--bg-surface)] border-l border-slate-200 dark:border-slate-700 w-72 shrink-0">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-200 dark:border-slate-700 shrink-0 bg-slate-50 dark:bg-[var(--bg-elevated)]">
        <div className="flex items-center gap-2 min-w-0">
          <Activity size={13} className="text-blue-500 shrink-0" />
          <div className="min-w-0">
            <p className="text-[11px] font-semibold text-slate-800 dark:text-slate-100">
              Mapa vs. Realidade
            </p>
            {blueprintName && (
              <p className="text-[9px] text-slate-400 truncate">{blueprintName}</p>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors shrink-0"
        >
          <X size={13} className="text-slate-400" />
        </button>
      </div>

      {/* Stats bar */}
      {totalExecutions > 0 && (
        <div className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800 shrink-0">
          <p className="text-[9px] text-blue-600 dark:text-blue-400">
            Baseado em{' '}
            <span className="font-bold">{totalExecutions}</span> execuções reais
          </p>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={18} className="animate-spin text-blue-500" />
          </div>
        )}

        {!isLoading && error && (
          <div className="flex items-start gap-2 p-3 m-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
            <AlertCircle size={13} className="text-red-500 shrink-0 mt-0.5" />
            <p className="text-[10px] text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {!isLoading && !error && !blueprintId && (
          <div className="p-4 text-center">
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Nenhum modelo vinculado.
            </p>
            <p className="text-[9px] text-slate-400 mt-1">
              Use "Publicar como Modelo" para vincular este mapa a um processo.
            </p>
          </div>
        )}

        {!isLoading && !error && blueprintId && rows.length === 0 && (
          <div className="p-4 text-center">
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Sem dados de execução disponíveis ainda.
            </p>
            <p className="text-[9px] text-slate-400 mt-1">
              Dados aparecerão após o processo ser executado.
            </p>
          </div>
        )}

        {!isLoading && !error && rows.length > 0 && (
          <>
            {/* Column headers */}
            <div className="flex items-center gap-1 px-3 py-1.5 text-[8px] uppercase tracking-wide text-slate-400 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-[var(--bg-elevated)]/50">
              <span className="flex-1">Etapa</span>
              <span className="w-11 text-right">Est.</span>
              <span className="w-11 text-right">Real</span>
              <span className="w-10 text-right">Δ</span>
            </div>
            {rows.map((row, i) => (
              <div
                key={i}
                className="flex items-start gap-1 px-3 py-2 border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-medium text-slate-700 dark:text-slate-200 truncate leading-tight">
                    {row.name}
                  </p>
                  {row.totalTasks > 0 && (
                    <p className="text-[8px] text-slate-400 mt-0.5">{row.totalTasks} exec.</p>
                  )}
                </div>
                <span className="text-[10px] text-slate-500 dark:text-slate-400 w-11 text-right shrink-0 pt-0.5">
                  {formatMin(row.estimatedTp)}
                </span>
                <span
                  className={`text-[10px] w-11 text-right shrink-0 pt-0.5 ${getDeltaClass(
                    row.estimatedTp,
                    row.realAvgTp
                  )}`}
                >
                  {formatMin(row.realAvgTp)}
                </span>
                <div className="w-10 flex items-start justify-end pt-0.5 gap-0.5">
                  {getDeltaIcon(row.estimatedTp, row.realAvgTp)}
                  <span
                    className={`text-[8px] ${getDeltaClass(row.estimatedTp, row.realAvgTp)}`}
                  >
                    {getDeltaPct(row.estimatedTp, row.realAvgTp)}
                  </span>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Footer legend */}
      {!isLoading && rows.length > 0 && (
        <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-700 shrink-0 bg-slate-50/50 dark:bg-[var(--bg-elevated)]/50">
          <div className="flex items-center gap-3 text-[8px] text-slate-400">
            <span className="flex items-center gap-0.5">
              <TrendingUp size={8} className="text-red-400" /> Acima estimado
            </span>
            <span className="flex items-center gap-0.5">
              <TrendingDown size={8} className="text-emerald-400" /> Abaixo estimado
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
